import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, CheckCircle2, FileWarning, Lock, Plus, Send, Undo2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import StaffRecordFiles from './StaffRecordFiles';
import staffRecordService, { errorText } from '../../services/staffRecords';
import {
  WARNING_LEVEL_LABEL,
  type StaffFile, type StaffRecordFile, type StaffWarning, type StaffWarningInput, type WarningLevel,
} from '../../types/staffRecords';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

const inputClass = 'w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

const STATUS_CHIP: Record<StaffWarning['status'], { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-neutral-100 text-neutral-700' },
  issued: { label: 'Not acknowledged yet', cls: 'bg-amber-100 text-amber-800' },
  acknowledged: { label: 'Acknowledged', cls: 'bg-green-100 text-green-800' },
  withdrawn: { label: 'Withdrawn', cls: 'bg-neutral-200 text-neutral-600' },
};

const LEVEL_CHIP: Record<WarningLevel, string> = {
  verbal: 'bg-sky-100 text-sky-800',
  written: 'bg-orange-100 text-orange-800',
  final: 'bg-red-100 text-red-800',
};

/**
 * Warnings to one employee, on the staff profile (Timesheet HR seat).
 *
 * Drafted, then issued: sent to the employee in FactreeTech, given in person,
 * or both. From issue the words are frozen (the server refuses a change) and
 * the employee acknowledges the exact text they were shown, with a response if
 * they want one. A paper acknowledgement is recorded here. Withdrawn, never
 * deleted.
 */
export default function StaffWarningsPanel({ userId, userName }: { userId: number; userName: string }) {
  const tz = useOrgTimezone();
  const [file, setFile] = useState<StaffFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setFile(await staffRecordService.getFile(userId));
    } catch (err) {
      setError(errorText(err, 'Could not load this person\'s warnings.'));
    }
  }, [userId]);

  useEffect(() => { setFile(null); load(); }, [load]);

  const warnings = file?.warnings ?? [];
  const inForce = warnings.filter(w => w.status === 'issued' || w.status === 'acknowledged');
  const filesOf = (id: number) => (file?.files ?? []).filter(f => f.warning_id === id);

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <FileWarning className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-neutral-900">Warnings</h3>
              <p className="text-sm text-neutral-500">
                {inForce.length === 0
                  ? `${userName} has no warnings on file.`
                  : `${inForce.length} warning${inForce.length === 1 ? '' : 's'} on file.`}
                {' '}{userName} sees a warning only once it is sent to them in FactreeTech.
              </p>
            </div>
          </div>
          {!adding && (
            <Button size="sm" onClick={() => { setAdding(true); setEditing(null); }} className="flex items-center gap-1">
              <Plus className="h-4 w-4" /> New warning
            </Button>
          )}
        </div>

        <p className="text-xs text-neutral-500">
          A warning should follow a conversation where {userName} heard the concern and had a chance to answer it.
          FactreeTech keeps the record; it does not decide whether a warning is fair or what your award,
          agreement or policies require. Check before relying on it.
        </p>

        {adding && (
          <WarningForm
            onCancel={() => setAdding(false)}
            onSave={async (input) => {
              await staffRecordService.addWarning(userId, input);
              setAdding(false);
              await load();
            }}
          />
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        {!file && !error && <p className="text-sm text-neutral-500">Loading…</p>}

        <ul className="space-y-3">
          {warnings.map(w => (
            <li key={w.id}>
              {editing === w.id ? (
                <WarningForm
                  initial={w}
                  onCancel={() => setEditing(null)}
                  onSave={async (input) => {
                    await staffRecordService.editWarning(w.id, input);
                    setEditing(null);
                    await load();
                  }}
                />
              ) : (
                <WarningCard
                  warning={w}
                  tz={tz}
                  userName={userName}
                  canSendInApp={file?.person.active_here !== false}
                  files={filesOf(w.id)}
                  onEdit={() => { setEditing(w.id); setAdding(false); }}
                  onChanged={load}
                />
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

type Action = null | 'issue' | 'record' | 'withdraw' | 'delete';

function WarningCard({ warning: w, tz, userName, canSendInApp, files, onEdit, onChanged }: {
  warning: StaffWarning;
  tz: string;
  userName: string;
  canSendInApp: boolean;
  files: StaffRecordFile[];
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [action, setAction] = useState<Action>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  // Issue
  const [sendInApp, setSendInApp] = useState(canSendInApp);
  const [inPerson, setInPerson] = useState(!canSendInApp);
  // Record acknowledgement / withdraw
  const [name, setName] = useState(userName);
  const [response, setResponse] = useState('');
  const [reason, setReason] = useState('');

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setAction(null);
      if (done) setNotice(done);
      onChanged();
    } catch (err) {
      setError(errorText(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS_CHIP[w.status];

  return (
    <div className={`border rounded-lg p-4 space-y-2 min-w-0 ${w.status === 'withdrawn' ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-200'}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className={`px-2 py-0.5 rounded-full font-medium ${LEVEL_CHIP[w.level]}`}>{WARNING_LEVEL_LABEL[w.level]}</span>
        <span className={`px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
        {w.issued_at && <span className="text-neutral-500">Issued {formatDateTimeInTz(w.issued_at, tz)}</span>}
      </div>
      <p className="font-semibold text-neutral-900 break-words">{w.subject}</p>

      {w.status === 'draft' ? (
        <div className="text-sm text-neutral-800 space-y-2">
          {w.incident_date && <p className="text-neutral-600">Date of the matter: {formatLocalDateInTz(w.incident_date, tz)}</p>}
          <Section heading="What happened" body={w.details} />
          <Section heading="What is expected from now on" body={w.expectations} />
          <Section heading="What happens if it continues" body={w.consequences} />
          <Section heading="Support offered" body={w.support} />
          <p className="text-xs text-neutral-500">Drafted by {w.created_by_name || 'someone'}. Nothing has been sent.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-neutral-600 break-words">
            {[
              w.sent_in_app && (w.first_read_at
                ? `Sent in FactreeTech, first read ${formatDateTimeInTz(w.first_read_at, tz)}`
                : 'Sent in FactreeTech, not opened yet'),
              w.given_in_person && 'Given in person',
              w.issued_by_name && `issued by ${w.issued_by_name}`,
            ].filter(Boolean).join(' · ')}
          </p>
          <button type="button" onClick={() => setShowText(s => !s)} className="text-sm text-primary-700 hover:underline">
            {showText ? 'Hide the warning as issued' : 'Show the warning as issued'}
          </button>
          {showText && w.issued_text && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 space-y-2">
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-800">{w.issued_text}</pre>
              <p className="text-xs text-neutral-500 break-all">Fingerprint (SHA-256): {w.issued_hash}</p>
            </div>
          )}
        </>
      )}

      {w.status === 'acknowledged' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900 space-y-1 min-w-0">
          <p className="flex items-start gap-1 break-words">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Acknowledged by {w.acknowledged_name}
              {w.acknowledged_at && `, ${formatDateTimeInTz(w.acknowledged_at, tz)}`}
              {w.acknowledged_method === 'in_app'
                ? ', in FactreeTech'
                : `, in person (recorded by ${w.acknowledgement_recorded_by_name || 'HR'})`}
            </span>
          </p>
          {w.employee_response
            ? <p className="whitespace-pre-wrap break-words"><span className="font-medium">Their response: </span>{w.employee_response}</p>
            : <p className="text-green-800">No response given.</p>}
        </div>
      )}

      {w.status === 'withdrawn' && (
        <p className="text-sm text-neutral-600 break-words">
          Withdrawn by {w.withdrawn_by_name || 'someone'}{w.withdrawn_at && `, ${formatDateTimeInTz(w.withdrawn_at, tz)}`}: {w.withdrawn_reason}
        </p>
      )}

      <div className="pt-1">
        <p className="text-xs font-medium text-neutral-500 flex items-center gap-1 mb-1">
          <Lock className="h-3 w-3" /> Files (HR only)
        </p>
        <StaffRecordFiles kind="warnings" ownerId={w.id} files={files} onChanged={onChanged} readOnly={w.status === 'withdrawn'} />
      </div>

      {action === null && (
        <div className="flex items-center gap-4 flex-wrap pt-1 text-sm">
          {w.status === 'draft' && (
            <>
              <button type="button" onClick={() => setAction('issue')} className="flex items-center gap-1 text-primary-700 font-medium hover:underline">
                <Send className="h-3.5 w-3.5" /> Issue
              </button>
              <button type="button" onClick={onEdit} className="text-primary-700 hover:underline">Edit</button>
              <button type="button" onClick={() => setAction('delete')} className="text-red-600 hover:underline">Delete draft</button>
            </>
          )}
          {w.status === 'issued' && (
            <>
              {w.sent_in_app && canSendInApp && (
                <button type="button" disabled={busy}
                  onClick={() => run(() => staffRecordService.remind(w.id), `${userName} has been reminded.`)}
                  className="flex items-center gap-1 text-primary-700 hover:underline disabled:opacity-50">
                  <Bell className="h-3.5 w-3.5" /> Remind
                </button>
              )}
              <button type="button" onClick={() => setAction('record')} className="text-primary-700 hover:underline">
                Record a signed acknowledgement
              </button>
            </>
          )}
          {(w.status === 'issued' || w.status === 'acknowledged') && (
            <button type="button" onClick={() => setAction('withdraw')} className="flex items-center gap-1 text-red-600 hover:underline">
              <Undo2 className="h-3.5 w-3.5" /> Withdraw
            </button>
          )}
        </div>
      )}
      {w.status === 'issued' && w.reminded_at && action === null && (
        <p className="text-xs text-neutral-500">Last reminded {formatDateTimeInTz(w.reminded_at, tz)}</p>
      )}

      {action === 'issue' && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-2 text-sm">
          <p className="font-medium text-neutral-900">How is {userName} getting this warning?</p>
          <label className={`flex items-start gap-2 ${canSendInApp ? 'cursor-pointer' : 'opacity-60'}`}>
            <input type="checkbox" checked={sendInApp} disabled={!canSendInApp} onChange={e => setSendInApp(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600" />
            <span>
              Send it in FactreeTech. They are notified, read it and acknowledge it there.
              {!canSendInApp && ` ${userName} no longer has access to this organisation.`}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={inPerson} onChange={e => setInPerson(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600" />
            <span>Given in person or on paper. Record their signature here afterwards.</span>
          </label>
          <p className="text-xs text-neutral-600 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Once issued its words cannot be changed. To correct it, withdraw it and issue another.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" disabled={busy || (!sendInApp && !inPerson)}
              onClick={() => run(() => staffRecordService.issue(w.id, { send_in_app: sendInApp, given_in_person: inPerson }),
                sendInApp ? `Sent to ${userName}.` : 'Issued.')}>
              {busy ? 'Issuing…' : 'Issue warning'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {action === 'record' && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-2 text-sm">
          <p className="text-neutral-700">For a warning {userName} signed on paper or acknowledged in a meeting. Attach the signed copy above.</p>
          <div>
            <label htmlFor={`ack-name-${w.id}`} className="block font-medium text-neutral-700 mb-1">Name they signed with</label>
            <input id={`ack-name-${w.id}`} className={inputClass} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor={`ack-response-${w.id}`} className="block font-medium text-neutral-700 mb-1">Their response, if they gave one</label>
            <textarea id={`ack-response-${w.id}`} rows={3} className={inputClass} value={response} onChange={e => setResponse(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" disabled={busy || name.trim().length < 2}
              onClick={() => run(() => staffRecordService.recordAcknowledgement(w.id, { name: name.trim(), response: response.trim() || null }))}>
              {busy ? 'Saving…' : 'Record acknowledgement'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {action === 'withdraw' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2 text-sm">
          <label htmlFor={`withdraw-${w.id}`} className="block font-medium text-red-800">Why is it being withdrawn?</label>
          <input id={`withdraw-${w.id}`} className={inputClass} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="For example: issued to the wrong person" />
          <p className="text-xs text-red-700">
            It stays on file marked withdrawn, with your reason.{w.sent_in_app ? ` ${userName} is told it has been withdrawn.` : ''}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="danger" disabled={busy || reason.trim().length < 3}
              onClick={() => run(() => staffRecordService.withdraw(w.id, reason.trim()))}>
              {busy ? 'Withdrawing…' : 'Withdraw warning'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {action === 'delete' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2 text-sm">
          <p className="text-red-800">Delete this draft? It was never sent.</p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="danger" disabled={busy} onClick={() => run(() => staffRecordService.deleteDraft(w.id))}>
              {busy ? 'Deleting…' : 'Delete draft'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAction(null)} disabled={busy}>Keep it</Button>
          </div>
        </div>
      )}

      {notice && <p className="text-sm text-green-700">{notice}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function Section({ heading, body }: { heading: string; body: string | null }) {
  if (!body) return null;
  return (
    <div className="min-w-0">
      <p className="font-medium text-neutral-700">{heading}</p>
      <p className="whitespace-pre-wrap break-words">{body}</p>
    </div>
  );
}

function WarningForm({ initial, onSave, onCancel }: {
  initial?: StaffWarning;
  onSave: (input: StaffWarningInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [level, setLevel] = useState<WarningLevel>(initial?.level ?? 'written');
  const [incidentDate, setIncidentDate] = useState(initial?.incident_date ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [details, setDetails] = useState(initial?.details ?? '');
  const [expectations, setExpectations] = useState(initial?.expectations ?? '');
  const [consequences, setConsequences] = useState(initial?.consequences ?? '');
  const [support, setSupport] = useState(initial?.support ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idp = initial ? `warning-${initial.id}` : 'warning-new';

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({
        level,
        incident_date: incidentDate || null,
        subject: subject.trim(),
        details: details.trim(),
        expectations: expectations.trim() || null,
        consequences: consequences.trim() || null,
        support: support.trim() || null,
      });
    } catch (err) {
      setError(errorText(err, 'The warning could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const field = (id: string, label: string, value: string, set: (v: string) => void, hint: string, rows = 3) => (
    <div>
      <label htmlFor={`${idp}-${id}`} className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <textarea id={`${idp}-${id}`} rows={rows} className={inputClass} value={value} onChange={e => set(e.target.value)} placeholder={hint} />
    </div>
  );

  return (
    <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <label htmlFor={`${idp}-level`} className="block text-sm font-medium text-neutral-700 mb-1">Kind of warning</label>
          <select id={`${idp}-level`} className={inputClass} value={level} onChange={e => setLevel(e.target.value as WarningLevel)}>
            {(Object.keys(WARNING_LEVEL_LABEL) as WarningLevel[]).map(l => <option key={l} value={l}>{WARNING_LEVEL_LABEL[l]}</option>)}
          </select>
        </div>
        <div className="min-w-0">
          <label htmlFor={`${idp}-date`} className="block text-sm font-medium text-neutral-700 mb-1">Date of the matter (optional)</label>
          <input id={`${idp}-date`} type="date" className={inputClass} value={incidentDate} onChange={e => setIncidentDate(e.target.value)} />
        </div>
      </div>
      <div>
        <label htmlFor={`${idp}-subject`} className="block text-sm font-medium text-neutral-700 mb-1">Subject</label>
        <input id={`${idp}-subject`} className={inputClass} value={subject} maxLength={200} onChange={e => setSubject(e.target.value)}
          placeholder="For example: Late to start three days running" />
      </div>
      {field('details', 'What happened', details, setDetails, 'The facts: dates, what was seen, what was discussed and what they said.', 4)}
      {field('expectations', 'What is expected from now on (optional)', expectations, setExpectations, 'What needs to change, and by when.')}
      {field('consequences', 'What happens if it continues (optional)', consequences, setConsequences, 'For example: a final warning, or the end of their employment.')}
      {field('support', 'Support offered (optional)', support, setSupport, 'Training, a change of roster, the employee assistance program.')}
      <p className="text-xs text-neutral-500">Saved as a draft. Nothing is sent until you issue it.</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" disabled={busy || subject.trim().length < 3 || !details.trim()} onClick={save}>
          {busy ? 'Saving…' : initial ? 'Save draft' : 'Save as draft'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}
